"use client";

import { useCallback, useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslations } from "next-intl";
import { Upload, FileText, X, AlertCircle, CheckCircle2, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

const ACCEPTED_TYPES = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
};

interface UploadedFile {
  file: File;
  id: string;
  progress: number;
  status: "pending" | "uploading" | "complete" | "error";
  error?: string;
}

interface DocumentUploaderProps {
  mode: "single" | "bulk";
  maxSizeMb?: number;
  maxTotalSizeMb?: number;
  onFilesSelected?: (files: File[]) => void;
  onUploadComplete?: (fileIds: string[]) => void;
}

export function DocumentUploader({
  mode = "single",
  maxSizeMb = 10,
  maxTotalSizeMb = 200,
  onFilesSelected,
}: DocumentUploaderProps) {
  const t = useTranslations("scan");
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: readonly { readonly file: File; readonly errors: readonly { message: string }[] }[]) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const newRejected = rejectedFiles.map((r) => ({
          file: r.file,
          id: crypto.randomUUID(),
          progress: 0,
          status: "error" as const,
          error: r.errors.map((e) => e.message).join(", "),
        }));
        setFiles((prev) => [...prev, ...newRejected]);
      }

      // Check total size for bulk mode
      if (mode === "bulk") {
        const currentTotal = files.reduce((sum, f) => sum + f.file.size, 0);
        const newTotal = acceptedFiles.reduce((sum, f) => sum + f.size, 0);
        if (currentTotal + newTotal > maxTotalSizeMb * 1024 * 1024) {
          return; // Would exceed total limit
        }
      }

      const newFiles = acceptedFiles.map((file) => ({
        file,
        id: crypto.randomUUID(),
        progress: 0,
        status: "pending" as const,
      }));

      if (mode === "single") {
        setFiles(newFiles.slice(0, 1));
      } else {
        setFiles((prev) => [...prev, ...newFiles]);
      }

      onFilesSelected?.(acceptedFiles);
    },
    [files, mode, maxTotalSizeMb, onFilesSelected]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: maxSizeMb * 1024 * 1024,
    maxFiles: mode === "single" ? 1 : 50,
    multiple: mode === "bulk",
  });

  const handleCameraCapture = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const capturedFiles = e.target.files;
      if (!capturedFiles || capturedFiles.length === 0) return;

      const fileArray = Array.from(capturedFiles);
      const newFiles = fileArray.map((file) => ({
        file,
        id: crypto.randomUUID(),
        progress: 0,
        status: "pending" as const,
      }));

      if (mode === "single") {
        setFiles(newFiles.slice(0, 1));
      } else {
        setFiles((prev) => [...prev, ...newFiles]);
      }

      onFilesSelected?.(fileArray);

      // Reset the input so the same file can be captured again if needed
      if (cameraInputRef.current) {
        cameraInputRef.current.value = "";
      }
    },
    [mode, onFilesSelected]
  );

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
  const totalPages = files.length; // rough estimate

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={cn(
          "relative rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-all duration-200",
          isDragActive
            ? "border-brand bg-brand/5"
            : "border-border bg-card hover:border-foreground/20 hover:bg-accent"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-3">
          <div
            className={cn(
              "rounded-full p-3 transition-colors",
              isDragActive ? "bg-brand/10" : "bg-secondary"
            )}
          >
            <Upload
              className={cn(
                "h-6 w-6",
                isDragActive ? "text-brand" : "text-muted-foreground"
              )}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              {isDragActive
                ? t("dropHere")
                : mode === "single"
                ? t("dragSingle")
                : t("dragBulk")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("acceptedFormats")}{" "}
              {mode === "single"
                ? t("maxSizeSingle", { size: maxSizeMb })
                : t("maxSizeBulk", { size: maxSizeMb, total: maxTotalSizeMb })}
            </p>
          </div>
          {mode === "bulk" && (
            <p className="text-[10px] text-muted-foreground/60">
              {t("bulkHint")}
            </p>
          )}
        </div>
      </div>

      {/* Camera capture button — shown on touch devices */}
      <div className="flex justify-center sm:hidden">
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleCameraCapture}
        />
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent transition-colors"
        >
          <Camera size={16} className="text-brand" />
          {t("takePhoto")}
        </button>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {mode === "bulk" && (
            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
              <span>
                {t("filesSelected", { count: files.filter((f) => f.status !== "error").length })}
              </span>
              <span>
                {formatSize(totalSize)}
                {totalPages > 1 && ` (${t("pages", { count: totalPages })})`}
              </span>
            </div>
          )}

          <div className="max-h-60 overflow-y-auto space-y-1.5 rounded-lg">
            {files.map((f) => (
              <div
                key={f.id}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm",
                  f.status === "error"
                    ? "bg-destructive/10 border border-destructive/30"
                    : "bg-card border border-border"
                )}
              >
                {f.status === "error" ? (
                  <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                ) : f.status === "complete" ? (
                  <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
                ) : (
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">
                    {f.file.name}
                  </p>
                  {f.status === "error" ? (
                    <p className="text-[10px] text-destructive">{f.error}</p>
                  ) : (
                    <p className="text-[10px] text-muted-foreground">
                      {formatSize(f.file.size)}
                    </p>
                  )}
                  {f.status === "uploading" && (
                    <Progress value={f.progress} className="mt-1 h-1" />
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(f.id);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
